import { getSeasonSummary } from "../../api/seasons";
import { useSeason } from "../../context/SeasonContext";
import { SeasonsSection, SeasonParticipantsSection } from "./SeasonsSection";
import { AuctionAdmin } from "../Auction/AuctionAdmin";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import { SeasonBar } from "../../components/Seasons/SeasonBar";
import { getAdminDashboard } from "../../api/admin";
import { useAuth } from "../../context/AuthContext";
import { useAdminData } from "./useAdminData";
import { fullName, number, roleLabel } from "./adminModel";
import { UsersSection, TeamsSection, RosterSection } from "./PeopleSections";
import {
  AchievementsSection,
  PrioritiesSection,
  ScheduleSection,
  ShowcaseSection,
} from "./ContentSections";
import { ImportSection } from "./ImportSection";
import { NewsSection } from "./NewsSection";
import { Button, Icon, Notice, SectionHeader } from "./components/AdminUI";
import styles from "./admin.module.css";

const NAVIGATION = [
  {
    label: "Рабочее пространство",
    items: [
      { id: "overview", label: "Обзор", icon: "overview" },
      { id: "seasons", label: "Сезоны", icon: "calendar" },
    ],
  },
  {
    label: "Участники и команды",
    items: [
      { id: "season-participants", label: "Участники сезона", icon: "users" },
      {
        id: "users",
        label: "Все аккаунты",
        icon: "users",
        count: "total_users",
      },
      { id: "teams", label: "Команды", icon: "teams", count: "total_teams" },
      { id: "public-teams", label: "Публичный список", icon: "globe" },
    ],
  },
  {
    label: "Контент",
    items: [
      { id: "news", label: "Новости", icon: "file" },
      { id: "showcase", label: "Витрина кейсов", icon: "cases" },
      { id: "auction", label: "Аукцион мерча", icon: "cases" },
      { id: "schedule", label: "Расписание", icon: "calendar" },
      { id: "achievements", label: "Достижения", icon: "award" },
    ],
  },
  {
    label: "Данные",
    items: [
      { id: "priorities", label: "Приоритеты", icon: "chart" },
      { id: "imports", label: "Импорт и выгрузки", icon: "upload" },
    ],
  },
];
const sectionIds = new Set(
  NAVIGATION.flatMap((group) => group.items.map((item) => item.id)),
);
const auctionNavigation = NAVIGATION.map((group) => ({
  ...group,
  items: group.items.filter((item) => item.id === "auction"),
})).filter((group) => group.items.length);
const emptyDashboard = async () => null;

const loadSeasonDashboard = async ({ seasonId }) => {
  const [globalData, seasonData] = await Promise.all([
    getAdminDashboard(),
    getSeasonSummary(seasonId),
  ]);
  return { ...globalData, ...seasonData };
};
function Overview({ dashboard, navigate }) {
  const cards = [
    {
      label: "Участники и заявки",
      key: "participants",
      icon: "users",
      hint: `Ожидают решения: ${number(dashboard.data?.pending)}`,
    },
    {
      label: "Команды",
      key: "teams",
      icon: "teams",
      hint: "В выбранном сезоне",
    },
    {
      label: "Кейсы",
      key: "cases",
      icon: "cases",
      hint: "Опубликованные и черновики",
    },
    {
      label: "События",
      key: "events",
      icon: "calendar",
      hint: "В программе сезона",
    },
  ];
  const shortcuts = [
    {
      label: "Найти участника",
      description: "Заявки, роли и результаты отбора",
      icon: "users",
      section: "season-participants",
    },
    {
      label: "Добавить кейс",
      description: "Новая проектная задача в витрине",
      icon: "cases",
      section: "showcase",
      action: "create",
    },
    {
      label: "Запланировать событие",
      description: "Встреча, занятие или контрольная точка",
      icon: "calendar",
      section: "schedule",
      action: "create",
    },
    {
      label: "Загрузить таблицу",
      description: "Команды и результаты отбора",
      icon: "upload",
      section: "imports",
    },
  ];
  return (
    <>
      <SectionHeader
        title="Обзор сезона"
        description="Всё для работы с интенсивом — участники, проекты и события в одном месте."
      >
        <Button
          icon="refresh"
          disabled={dashboard.loading}
          onClick={dashboard.refresh}
        >
          Обновить
        </Button>
      </SectionHeader>
      {dashboard.error && <Notice danger>{dashboard.error}</Notice>}
      <div className={styles.statsGrid}>
        {cards.map((card) => (
          <article key={card.key} className={styles.statCard}>
            <div className={styles.statTop}>
              <span>{card.label}</span>
              <Icon name={card.icon} size={17} />
            </div>
            <p className={styles.statValue}>
              {dashboard.loading
                ? "…"
                : dashboard.data
                  ? number(dashboard.data[card.key])
                  : "—"}
            </p>
            <span className={styles.hint}>{card.hint}</span>
          </article>
        ))}
      </div>
      <div className={styles.overviewGrid}>
        <section className={`${styles.panel} ${styles.overviewPanel}`}>
          <div className={styles.blockTitle}>
            <h2>Частые действия</h2>
            <small>Быстрый переход</small>
          </div>
          <div className={styles.quickGrid}>
            {shortcuts.map((shortcut) => (
              <button
                type="button"
                key={shortcut.label}
                className={styles.quickLink}
                onClick={() => navigate(shortcut.section, shortcut.action)}
              >
                <Icon name={shortcut.icon} size={21} />
                <strong>{shortcut.label}</strong>
                <span>{shortcut.description}</span>
              </button>
            ))}
          </div>
        </section>
        <section className={`${styles.panel} ${styles.overviewPanel}`}>
          <div className={styles.blockTitle}>
            <h2>Работа с интенсивом</h2>
          </div>
          {[
            {
              title: "Подготовить проекты",
              text: "Добавьте кейсы и проверьте их публикацию.",
              section: "showcase",
            },
            {
              title: "Проверить выбор участников",
              text: "Посмотрите приоритеты перед формированием команд.",
              section: "priorities",
            },
            {
              title: "Организовать команды",
              text: "Назначьте капитанов, участников и проектные задачи.",
              section: "teams",
            },
            {
              title: "Обновить программу",
              text: "События, записи встреч и задания для цифрового следа.",
              section: "schedule",
            },
          ].map((item, index) => (
            <Link
              key={item.section}
              to={`/admin?section=${item.section}`}
              className={styles.workflowItem}
            >
              <span className={styles.workflowNumber}>0{index + 1}</span>
              <div>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </div>
              <Icon name="arrow" size={16} />
            </Link>
          ))}
        </section>
      </div>
      {dashboard.updatedAt && (
        <p className={styles.overviewFooter}>
          <Icon name="check" size={13} />
          Данные обновлены в{" "}
          {dashboard.updatedAt.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      )}
    </>
  );
}

export function AdminPage({ auctionOnly = false }) {
  const { user } = useAuth();
  const { season, withSeason } = useSeason();
  const [params, setParams] = useSearchParams();
  const section = auctionOnly ? "auction" : sectionIds.has(params.get("section"))
    ? params.get("section")
    : "overview";
  const dashboard = useAdminData(
    auctionOnly ? emptyDashboard : season?.id ? loadSeasonDashboard : getAdminDashboard,
    season?.id ? { seasonId: season.id } : {},
  );
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!message) return undefined;
    const timeout = window.setTimeout(() => setMessage(""), 8000);
    return () => window.clearTimeout(timeout);
  }, [message]);
  const navigate = (next, action) =>
    setParams({
      section: next,
      ...(season ? { season: season.id } : {}),
      ...(action ? { action } : {}),
    });
  const clearAction = () =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("action");
        return next;
      },
      { replace: true },
    );
  const sectionProps = {
    notify: setMessage,
    refreshDashboard: dashboard.refresh,
    navigate,
    initialAction: params.get("action"),
    clearAction,
  };
  return (
    <section className={styles.page}>
      <header className={styles.masthead}>
        <div className={styles.brand}>
          <span className={styles.brandIcon}>
            <Icon name="shield" size={21} />
          </span>
          <div>
            <strong>
              ЮФУ.Про <span className={styles.cellMeta}>/ Управление</span>
            </strong>
            <small>Панель администратора</small>
          </div>
        </div>
        <div className={styles.seasonControls}>
          <SeasonBar compact admin />
        </div>
        <div className={styles.mastheadRight}>
          <Link to="/" className={styles.siteLink}>
            На сайт <Icon name="external" size={14} />
          </Link>
          <Link to="/profile" className={styles.profileLink}>
            <span className={styles.avatar}>
              {(user?.first_name || user?.login || "А")[0]}
            </span>
            <div>
              <strong>{fullName(user)}</strong>
              <small>{auctionOnly ? "Администратор аукциона" : roleLabel(user?.role)}</small>
            </div>
          </Link>
        </div>
      </header>
      <div className={styles.workspace}>
        <nav className={styles.sidebar} aria-label="Разделы админ-панели">
          {(auctionOnly ? auctionNavigation : NAVIGATION).map((group) => (
            <div className={styles.navGroup} key={group.label}>
              <p className={styles.navGroupLabel}>{group.label}</p>
              {group.items.map((item) => (
                <Link
                  key={item.id}
                  to={withSeason(`/admin?section=${item.id}`)}
                  aria-current={section === item.id ? "page" : undefined}
                  className={`${styles.navLink} ${section === item.id ? styles.navActive : ""}`}
                >
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                  {item.count && dashboard.data && (
                    <span className={styles.navCount}>
                      {number(dashboard.data[item.count])}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ))}
          <div className={styles.sidebarNote}>
            <strong>ЮФУ.Про</strong>Управление проектным интенсивом
          </div>
        </nav>
        <div className={styles.content} key={section}>
          {section === "overview" && (
            <Overview dashboard={dashboard} navigate={navigate} />
          )}
          {section === "seasons" && <SeasonsSection {...sectionProps} />}
          {section === "auction" && <AuctionAdmin />}
          {section === "season-participants" && (
            <SeasonParticipantsSection {...sectionProps} />
          )}
          {section === "users" && <UsersSection {...sectionProps} />}
          {section === "teams" && <TeamsSection {...sectionProps} />}
          {section === "public-teams" && <RosterSection {...sectionProps} />}
          {section === "showcase" && <ShowcaseSection {...sectionProps} />}
          {section === "news" && <NewsSection {...sectionProps} />}
          {section === "schedule" && <ScheduleSection {...sectionProps} />}
          {section === "achievements" && (
            <AchievementsSection {...sectionProps} />
          )}
          {section === "priorities" && <PrioritiesSection {...sectionProps} />}
          {section === "imports" && <ImportSection {...sectionProps} />}
        </div>
      </div>
      {message && (
        <div className={styles.toast}>
          <Notice onClose={() => setMessage("")}>{message}</Notice>
        </div>
      )}
    </section>
  );
}
